
import { supabase } from "@/integrations/supabase/client";

// Temporary solution using localStorage while we fix the Supabase Edge Function
const API_KEYS_STORAGE_KEY = "reheat_api_keys";

const getStoredApiKeys = (): Record<string, string> => {
  const keys = localStorage.getItem(API_KEYS_STORAGE_KEY);
  return keys ? JSON.parse(keys) : {};
};

const storeApiKey = (provider: string, apiKey: string): void => {
  const keys = getStoredApiKeys();
  keys[provider] = apiKey;
  localStorage.setItem(API_KEYS_STORAGE_KEY, JSON.stringify(keys));
};

const getStoredApiKey = (provider: string): string | null => {
  const keys = getStoredApiKeys();
  return keys[provider] || null;
};

export const setProviderApiKey = async (
  provider: string,
  apiKey: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`Attempting to set API key for provider: ${provider}`);
    
    // First attempt to store locally
    storeApiKey(provider, apiKey);
    
    // Also try to store in Supabase, but don't fail if it doesn't work
    try {
      const { data, error } = await supabase.functions.invoke("set-provider-api-key", {
        body: { provider, apiKey },
      });
      
      if (error) {
        console.warn("Supabase function invocation warning (continuing anyway):", error);
      }
    } catch (err) {
      console.warn("Failed to set API key via Supabase (continuing anyway):", err);
    }
    
    return { success: true };
  } catch (error) {
    console.error(`Error setting API key for provider ${provider}:`, error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unknown error" 
    };
  }
};

export const checkProviderConnection = async (
  provider: string
): Promise<boolean> => {
  try {
    console.log(`Checking connection for provider: ${provider}`);
    
    // First check if we have a locally stored key
    const storedKey = getStoredApiKey(provider);
    if (storedKey) {
      console.log(`Found locally stored key for ${provider}`);
      return true;
    }
    
    // Also try to check via Supabase, but don't fail if it doesn't work
    try {
      const { data, error } = await supabase.functions.invoke("check-provider-connection", {
        body: { provider },
      });
      
      if (error) {
        console.warn("Supabase function invocation warning:", error);
        return false;
      }
      
      return data?.isConnected || false;
    } catch (err) {
      console.warn("Failed to check connection via Supabase:", err);
      return false;
    }
  } catch (error) {
    console.error(`Error checking connection for provider ${provider}:`, error);
    return false;
  }
};

// Helper function to get API key for external use
export const getProviderApiKey = (provider: string): string | null => {
  return getStoredApiKey(provider);
};

